import { ComponentInfo, SERIAL_NUMBER_REGEXPS } from "./models";

/**
 * Creates a ComponentInfo from a serialNumber (string).
 * @param {string} serialNumber
 * @returns {ComponentInfo}
 */
export function getPartTypeFromSerialNumber(
  serialNumber: string,
): ComponentInfo {
  const compnentInfo: ComponentInfo = {
    serialNumber,
    moduleType: "Unknown",
    componentType: "Unknown",
    componentSubtype: "None",
    orientation: "N/A",
    shorthand: "Unknown",
    partTypeId: "Unknown",
  };

  if (!serialNumber) return compnentInfo;

  if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_2S.MODULE)) {
    compnentInfo.moduleType = "2S";
    compnentInfo.componentType = "Module";
    compnentInfo.shorthand = "2S Module";
    compnentInfo.partTypeId = "2S.Module";
  }
  if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_PS.MODULE)) {
    compnentInfo.moduleType = "PS";
    compnentInfo.componentType = "Module";
    compnentInfo.shorthand = "PS Module";
    compnentInfo.partTypeId = "PS.Module";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_2S.SENSOR)) {
    compnentInfo.moduleType = "2S";
    compnentInfo.componentType = "Sensor";
    compnentInfo.shorthand = "2S Sensor";
    compnentInfo.partTypeId = "2S.Sensor";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_2S.SEH)) {
    compnentInfo.moduleType = "2S";
    compnentInfo.componentType = "Hybrid";
    compnentInfo.componentSubtype = "SEH";
    compnentInfo.shorthand = "2S SEH";
    compnentInfo.partTypeId = "2S.SEH";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_2S.FEH)) {
    const componentId = serialNumber.split("-")[0];
    compnentInfo.moduleType = "2S";
    compnentInfo.componentType = "Hybrid";
    compnentInfo.componentSubtype = "FEH";
    if (componentId[componentId.length - 1] === "L")
      compnentInfo.orientation = "Left";
    if (componentId[componentId.length - 1] === "R")
      compnentInfo.orientation = "Right";
    compnentInfo.shorthand = "2SFEH";
    //  "2S FEH" + compnentInfo.orientation
    //    ? ` (${compnentInfo.orientation})`
    //    : "";
    compnentInfo.partTypeId = "2S.FEH";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_PS.POH)) {
    compnentInfo.moduleType = "PS";
    compnentInfo.componentType = "Hybrid";
    compnentInfo.componentSubtype = "POH";
    compnentInfo.shorthand = "PS POH";
    compnentInfo.partTypeId = "PS.POH";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_PS.ROH)) {
    compnentInfo.moduleType = "PS";
    compnentInfo.componentType = "Hybrid";
    compnentInfo.componentSubtype = "ROH";
    compnentInfo.shorthand = "PS ROH";
    compnentInfo.partTypeId = "PS.ROH";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_PS.FEH)) {
    const componentId = serialNumber.split("-")[0];
    compnentInfo.moduleType = "PS";
    compnentInfo.componentType = "Hybrid";
    compnentInfo.componentSubtype = "FEH";
    if (componentId[componentId.length - 1] === "L")
      compnentInfo.orientation = "Left";
    if (componentId[componentId.length - 1] === "R")
      compnentInfo.orientation = "Right";
    compnentInfo.shorthand = "PSFEH";
    //compnentInfo.shorthand =
    //  "PS FEH" + compnentInfo.orientation
    //    ? ` (${compnentInfo.orientation})`
    //    : "";
    compnentInfo.partTypeId = "PS.FEH";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_PS.MAPSA)) {
    compnentInfo.moduleType = "PS";
    compnentInfo.componentType = "Sensor";
    compnentInfo.componentSubtype = "MaPSA";
    compnentInfo.shorthand = "MaPSA";
    compnentInfo.partTypeId = "PS.MaPSA";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_PS.SENSOR)) {
    compnentInfo.moduleType = "PS";
    compnentInfo.componentType = "Sensor";
    compnentInfo.shorthand = "PS-s";
    compnentInfo.partTypeId = "PS.Sensor";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_PS.CFBASEPLATE)) {
    compnentInfo.moduleType = "PS";
    compnentInfo.componentType = "Baseplate";
    compnentInfo.shorthand = "CF Baseplate";
    compnentInfo.partTypeId = "PS.CFBaseplate";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_PS.VTRX)) {
    compnentInfo.moduleType = "PS";
    compnentInfo.componentType = "Hybrid";
    compnentInfo.componentSubtype = "VTRX";
    compnentInfo.shorthand = "VTRX";
    compnentInfo.partTypeId = "PS.VTRX";
  } else if (serialNumber.match(SERIAL_NUMBER_REGEXPS.MODULE_2S.VTRX)) {
    compnentInfo.moduleType = "2S";
    compnentInfo.componentType = "Module";
    compnentInfo.componentSubtype = "VTRX";
    compnentInfo.shorthand = "VTRX";
    compnentInfo.partTypeId = "2S.VTRX";
  }
  return compnentInfo;
}

/**
 * Formats a ComponentInfo as a string
 * @param {ComponentInfo} info info about a serial number
 * @returns {string} Description of the component.
 */
export function formatComponentInfo(info: ComponentInfo): string {
  const componentType =
    info.componentType !== "Unknown" && info.componentType !== "Hybrid"
      ? ` ${info.componentType}`
      : "";
  const componentSubtype =
    info.componentSubtype !== "None" ? ` ${info.componentSubtype}` : "";
  const orientation =
    info.orientation !== "N/A" ? ` (${info.orientation})` : "";

  return `${info.moduleType}${componentType}${componentSubtype}${orientation}`;
}

/**
 * Identifies a part and gives a description of it.
 * @param {string} serialNumber serialNumber being analyzed.
 * @returns {string} description of the part for that serial.
 */
export function getComponentDescription(serialNumber: string) {
  return formatComponentInfo(getPartTypeFromSerialNumber(serialNumber));
}

/**
 * Gives the type of module based on the ID of the module.
 * @param {string} moduleId ID for the module.
 * @returns {string} "PS" or "2S"
 */
export function getModuleTypeFromId(moduleId: string) {
  const partType = getPartTypeFromSerialNumber(moduleId);
  return partType.moduleType;
}

/**
 * Converts a step name as defined by PRODUCTION_STEPS_2S and
 * PRODUCTION_STEPS_PS and returns a more human readable version.
 * @param {string} step step name
 * @returns {string} step name with spaces and first letter caps.
 */
export function getHumanReadableStepName(step: string): string {
  const CAPS_TOKENS = [
    "2S",
    "PS",
    "FEH",
    "SEH",
    "ROH",
    "POH",
    "CF",
    "HV",
    "IV",
    "VTRX",
  ];
  return step
    .replace("AUX_", "")
    .split("_")
    .map((word) => (CAPS_TOKENS.includes(word) ? word : word.toLowerCase()))
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .reduce((stepName, stepWord) => stepName + " " + stepWord, "")
    .replace("Ma Psa", "MaPSA")
    .replace("VTRX", "VTRx+")
    .trim();
}
